import { createApi } from "@reduxjs/toolkit/query/react";
import { GridFilterModel } from "@mui/x-data-grid-pro";
import fetchBaseQueryCSRF from "../helper";
import { ALL_REGIONS, ClusterModule } from "src/sections/cluster-manager/config";

/** Module whose clusters are read/written, and the country tab (ALL = every region). */
export type ClusterScopeArgs = { module: ClusterModule; region?: string };

export type ClusterGridParams = ClusterScopeArgs & {
    workspaceId: number;
    start: string;
    end: string;
    keywords: string[];
    devices: string[];
    platforms: string[];
    page: number;
    pageSize: number;
    filterModel?: GridFilterModel;
    tags?: string[];
    sortField?: string;
    sortOrder?: string;
};

export type BulkTagArgs = ClusterScopeArgs & {
    workspaceId: number;
    addTags: { label: string }[];
    searchTerms?: { id?: string; label: string }[];
    start?: string;
    end?: string;
    keywords?: string[];
    devices?: string[];
    platforms?: string[];
    tags?: string[];
    filterModel?: GridFilterModel;
};

const ALL_TAGS = ['TagsListBySearchTerm', 'TagsList', 'Tags', 'SearchTermsByTags'] as const;

const scopeParams = ({ module, region }: ClusterScopeArgs) => ({ module, region: region ?? ALL_REGIONS });

const hasFilters = (filterModel?: GridFilterModel) => !!filterModel && filterModel.items.length > 0;

const gridQuery = (url: string) => ({ workspaceId, filterModel, module, region, ...params }: ClusterGridParams) => ({
    url: `${url}?workspace_id=${workspaceId}`,
    method: hasFilters(filterModel) ? "POST" : "GET",
    params: { ...params, ...scopeParams({ module, region }) },
    body: hasFilters(filterModel) ? filterModel : undefined,
});

export const tagAPI = createApi({
    reducerPath: 'tagAPI',
    tagTypes: [...ALL_TAGS],
    baseQuery: fetchBaseQueryCSRF({ baseUrl: process.env.CROSS_API_URL + '/tags-manager' }),
    endpoints: (builder) => ({
        getTags: builder.query<any, ClusterGridParams>({
            query: gridQuery('/search-terms'),
            providesTags: ['Tags'],
        }),
        getFilterdSummary: builder.query<any, ClusterGridParams>({
            query: gridQuery('/filterd-summary'),
            providesTags: ['Tags'],
        }),
        getTagsList: builder.query<any, { workspaceId: number } & ClusterScopeArgs>({
            query: ({ workspaceId, ...scope }) => ({
                url: `/?workspace_id=${workspaceId}`,
                params: scopeParams(scope),
            }),
            providesTags: ['TagsList'],
        }),
        getTagsForSearchTerm: builder.query<any, { workspaceId: number; searchTerm: string } & ClusterScopeArgs>({
            query: ({ workspaceId, searchTerm, ...scope }) => ({
                url: `/search-term/tags?workspace_id=${workspaceId}`,
                params: { search_term: searchTerm, ...scopeParams(scope) },
            }),
            providesTags: ['TagsListBySearchTerm'],
        }),
        tagSearchTerm: builder.mutation<any, { workspaceId: number; searchTerm: string; tags: { label: string }[] } & ClusterScopeArgs>({
            query: ({ workspaceId, searchTerm, tags, ...scope }) => ({
                url: `/search-term/tag/bulk?workspace_id=${workspaceId}`,
                method: "POST",
                params: scopeParams(scope),
                body: { tags, search_term: searchTerm },
            }),
            invalidatesTags: [...ALL_TAGS],
        }),
        tagSearchTermsWithTagsBulk: builder.mutation<any, BulkTagArgs>({
            query: ({ workspaceId, module, region, addTags, searchTerms, filterModel, ...filters }) => ({
                url: `/search-terms/tag/bulk?workspace_id=${workspaceId}`,
                method: "POST",
                params: { ...filters, ...scopeParams({ module, region }) },
                body: { tags: addTags, search_terms: searchTerms, filter_model: filterModel },
            }),
            invalidatesTags: [...ALL_TAGS],
        }),
        untagSearchTerm: builder.mutation<any, { workspaceId: number; searchTerm: string; tag: string } & ClusterScopeArgs>({
            query: ({ workspaceId, searchTerm, tag, ...scope }) => ({
                url: `/search-term/untag?workspace_id=${workspaceId}`,
                method: "PUT",
                params: scopeParams(scope),
                body: { search_term: searchTerm, name: tag },
            }),
            invalidatesTags: [...ALL_TAGS],
        }),
        getSearchTermsByTags: builder.query<{ search_terms: { id: number, name: string }[] }, { workspaceId: number; tags: string[] } & ClusterScopeArgs>({
            query: ({ workspaceId, tags, ...scope }) => ({
                url: `/search-terms-by-tags`,
                // `params` gets URL-encoded: cluster names may contain reserved characters (e.g. "coupons & promo")
                params: { workspace_id: workspaceId, tags: tags.join(","), ...scopeParams(scope) },
            }),
            providesTags: ['SearchTermsByTags'],
        }),
        bulkRemoveClusters: builder.mutation<any, { workspaceId: number; tags: string[]; searchTerms: string[] } & ClusterScopeArgs>({
            query: ({ workspaceId, tags, searchTerms, ...scope }) => ({
                url: `/search-terms/untag/bulk?workspace_id=${workspaceId}`,
                method: "PUT",
                params: scopeParams(scope),
                body: { tags, search_terms: searchTerms },
            }),
            invalidatesTags: [...ALL_TAGS],
        }),
    }),
});

export const {
    useGetTagsQuery,
    useGetTagsListQuery,
    useGetTagsForSearchTermQuery,
    useTagSearchTermMutation,
    useTagSearchTermsWithTagsBulkMutation,
    useUntagSearchTermMutation,
    useGetSearchTermsByTagsQuery,
    useGetFilterdSummaryQuery,
    useBulkRemoveClustersMutation
} = tagAPI;
export default tagAPI;
